import { useState } from 'react';
import { Button, Modal } from '@sandtech-hub-monorepo/ui-kit';
import {
  HighSchoolVerificationMode,
  PersonaInquiryAction,
} from '@sandtech-hub-monorepo/ehub-client';
import { VerificationStatus } from './verificationUtils';
import { PersonaHighSchoolVerification } from './modals/PersonaHighSchoolVerification';

interface VerificationStepProps {
  programApplicationId?: string;
  title: string;
  description: string;
  pendingDescription?: string;
  failedDescription?: string;
  completedDescription: string;
  resumeDescription?: string;
  status: VerificationStatus;
  onModalOpen: () => void;
  managePersonaInquiryMutation?: (action: PersonaInquiryAction) => Promise<any>;
  actionText?: string;
  isDisabled?: boolean;
  disabledReason?: string;
  verificationMode?: HighSchoolVerificationMode;
  showButtonWhenCompleted?: boolean;
}

export const VerificationStep = ({
  programApplicationId,
  title,
  description,
  pendingDescription,
  failedDescription,
  completedDescription,
  resumeDescription,
  status,
  onModalOpen,
  managePersonaInquiryMutation,
  actionText,
  isDisabled = false,
  disabledReason,
  verificationMode,
  showButtonWhenCompleted = false,
}: VerificationStepProps) => {
  const [fallBackModalOpen, setFallbackModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isCompleted = status === VerificationStatus.Completed;
  const isPending = status === VerificationStatus.Pending;
  const isFailed = status === VerificationStatus.Failed;
  const isNotSubmitted = status === VerificationStatus.NotSubmitted;
  const isResume = status === VerificationStatus.Resume;
  const showButton =
    ((isNotSubmitted || isResume || isFailed) && !isDisabled) ||
    (isCompleted && showButtonWhenCompleted);

  // Determine button text based on status
  const getActionText = () => {
    if (loading) {
      if (isResume) return 'Resuming...';
      if (isFailed) return 'Resubmitting...';
      return 'Submitting...';
    }

    if (isResume) return 'Resume';
    if (isFailed) return 'Resubmit';
    if (actionText) return actionText;
    return 'Submit now';
  };

  // Determine button click handler based on status
  const handleClick = () => {
    if (isResume) {
      // For Resume status, only call the mutation with Resume action
      setLoading(true);
      managePersonaInquiryMutation?.(PersonaInquiryAction.Resume)?.finally(() =>
        setLoading(false)
      );
    } else if (isFailed) {
      // For Failed status, call the mutation with Generate action
      setLoading(true);
      managePersonaInquiryMutation?.(PersonaInquiryAction.Generate)?.finally(
        () => setLoading(false)
      );
    } else if (isNotSubmitted || showButtonWhenCompleted) {
      // For NotSubmitted status, call onModalOpen
      onModalOpen();
    }
  };

  return (
    <div className="relative flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <div
          className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center ${
            isCompleted || !isDisabled
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-400'
          }`}
        >
          {isCompleted ? '✓' : '•'}
        </div>
        <div>
          <div
            className={`font-medium text-sm ${
              isCompleted || !isDisabled
                ? 'text-foreground'
                : 'text-foreground/40'
            }`}
          >
            {title}
          </div>
          <div
            className={`text-xs ${
              isPending
                ? 'text-warning'
                : isFailed
                ? 'text-destructive'
                : isResume
                ? 'text-primary'
                : isCompleted || !isDisabled
                ? 'text-foreground/60'
                : 'text-foreground/40'
            }`}
          >
            {isCompleted
              ? completedDescription
              : isPending && pendingDescription
              ? pendingDescription
              : isFailed && failedDescription
              ? failedDescription
              : isResume && resumeDescription
              ? resumeDescription
              : isDisabled && disabledReason
              ? disabledReason
              : description}
          </div>
          {programApplicationId &&
            verificationMode === HighSchoolVerificationMode.Manual &&
            (isFailed || isNotSubmitted) && (
              <>
                <p className="text-muted-foreground text-xs">
                  You can still verify by{' '}
                  <span
                    className="underline cursor-pointer"
                    onClick={() => setFallbackModalOpen(true)}
                  >
                    uploading your certificate
                  </span>
                </p>
                <Modal
                  open={fallBackModalOpen}
                  onOpenChange={setFallbackModalOpen}
                >
                  <PersonaHighSchoolVerification
                    programApplicationId={programApplicationId}
                    close={() => setFallbackModalOpen(false)}
                    hasChosenPersona={true}
                  />
                </Modal>
              </>
            )}
        </div>
      </div>
      {showButton && (
        <Button variant="outline" onClick={handleClick} disabled={loading}>
          {getActionText()}
        </Button>
      )}
    </div>
  );
};
